import { useEffect, useState } from 'react'
import { FetchStatus } from '../../../../types/fetch-status.types'
import constants from '../../../../constants'
import { MedicalReimbursement } from '../../../../database/tables'
import { GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid'
import MedicalReimbursementService from '../../../../services/medical-reimbursement'
import { ReimbursementType } from '../../../../database/enums/ReimbursementType.enum'
import ReimbursementTypeEnumHelper from '../../../../database/enums/helpers/reimbursement-type.helper'
import CurrencyEnumHelper from '../../../../database/enums/helpers/currency.helper'
import { ApprovalStatus } from '../../../../database/enums/ApprovalStatus.enum'
import ApprovalStatusEnumHelper from '../../../../database/enums/helpers/approval-status.helper'
import { useNavigate } from 'react-router'
import links from '../../../../routes/links'
import InfoIcon from '@mui/icons-material/Info'
import IconEdit from '@mui/icons-material/Edit'

const { defaultActionWidth, defaultBaseWidth, defaultDateTimeWidth, defaultDateWidth } = constants.dataGrid
export const scopeNamePrefix = 'admin-medical-reimbursements-unresponded'

export default function useAdminMedicalReimbursementsRespondedPageViewModel() {
  const navigate = useNavigate()

  const [dataFetchStatus, setDataFetchStatus] = useState<FetchStatus>(constants.fetchStatus.default)
  const [medicalReimbursements, setMedicalReimbursements] = useState<MedicalReimbursement[]>([])

  const tableColumns: GridColDef<MedicalReimbursement>[] = [
    {
      field: 'approvalStatus',
      headerName: 'Status',
      minWidth: defaultBaseWidth,
      type: 'singleSelect',
      valueOptions: Object.values(ApprovalStatus),
      getOptionLabel: value => ApprovalStatusEnumHelper.getLabel(value as ApprovalStatus),
    },
    { field: 'name', headerName: 'Name', editable: true, minWidth: defaultBaseWidth, type: 'string' },
    { field: 'email', headerName: 'Email', editable: true, minWidth: defaultBaseWidth, type: 'string' },
    { field: 'serviceDate', headerName: 'Service Date', editable: true, minWidth: defaultDateWidth, type: 'date' },
    {
      field: 'reimbursementType',
      headerName: 'Type',
      minWidth: defaultBaseWidth,
      type: 'singleSelect',
      valueOptions: Object.values(ReimbursementType),
      getOptionLabel: value => ReimbursementTypeEnumHelper.getLabel(value as ReimbursementType),
    },
    { field: 'vendor', headerName: 'Vendor', editable: true, minWidth: defaultBaseWidth, type: 'string' },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      editable: true,
      minWidth: defaultBaseWidth,
      type: 'string',
    },
    {
      field: 'totalAmountClaimed',
      headerName: 'Total Amount Claimed',
      flex: 1,
      minWidth: defaultBaseWidth,
      type: 'string',
      valueGetter: (value: MedicalReimbursement['totalAmountClaimed']) =>
        `${CurrencyEnumHelper.getSymbol(value.currency)} ${Intl.NumberFormat().format(value.value)}`,
    },
    { field: 'createdAt', headerName: 'Created At', editable: true, minWidth: defaultDateTimeWidth, type: 'dateTime' },
    {
      field: 'approvalUpdatedAt',
      headerName: 'Approval At',
      editable: true,
      minWidth: defaultDateTimeWidth,
      type: 'dateTime',
    },
    { field: 'approvalBy', headerName: 'Approval By', editable: true, minWidth: defaultBaseWidth, type: 'string' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: defaultActionWidth,
      type: 'actions',
      getActions: (params: GridRowParams<MedicalReimbursement>) => [
        <GridActionsCellItem
          key={`${scopeNamePrefix}-table-actions-${params.id}-info`}
          icon={<InfoIcon color='info' />}
          label='Info'
          onClick={() => navigate(links.admin.medicalReimbursementDetail({ id: params.row.id }))}
        />,
        <GridActionsCellItem
          key={`${scopeNamePrefix}-table-actions-${params.id}-edit`}
          icon={<IconEdit color='warning' />}
          label='Edit'
          onClick={() => navigate(links.admin.medicalReimbursementEdit({ id: params.row.id }))}
        />,
      ],
    },
  ]

  async function fetchMedicalReimbursementsRespondedData() {
    setDataFetchStatus({ ...constants.fetchStatus.default, isLoading: true })
    await MedicalReimbursementService.getResponded()
      .then(res => {
        setMedicalReimbursements(res)
        setDataFetchStatus({ ...constants.fetchStatus.default, isSuccess: true })
      })
      .catch(error => {
        console.log(error)
        setDataFetchStatus({ ...constants.fetchStatus.default, isError: true })
      })
  }

  useEffect(() => {
    fetchMedicalReimbursementsRespondedData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    dataFetchStatus,
    fetchMedicalReimbursementsRespondedData,
    tableData: {
      columns: tableColumns,
      rows: medicalReimbursements,
    },
  }
}
